import React from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  rating: number;
  onRatingChange?: (rating: number) => void;
  readonly?: boolean;
  size?: "sm" | "md" | "lg";
  showNumber?: boolean;
  className?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  onRatingChange,
  readonly = false,
  size = "md",
  showNumber = false,
  className,
}) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const handleStarClick = (starRating: number) => {
    if (!readonly && onRatingChange) {
      onRatingChange(starRating);
    }
  };

  const stars = Array.from({ length: 5 }, (_, index) => {
    const starNumber = index + 1;
    const isActive = starNumber <= rating;
    const isPartial = !isActive && starNumber - 1 < rating && rating < starNumber;

    return (
      <button
        key={index}
        type="button"
        onClick={() => handleStarClick(starNumber)}
        disabled={readonly}
        className={cn(
          "relative",
          !readonly && "hover:scale-110 transition-transform",
          readonly && "cursor-default"
        )}
      >
        <Star
          className={cn(
            sizeClasses[size],
            isActive || isPartial
              ? "fill-yellow-400 text-yellow-400"
              : "fill-gray-200 text-gray-200",
            !readonly && "hover:fill-yellow-300 hover:text-yellow-300"
          )}
        />
        {isPartial && (
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${((rating - (starNumber - 1)) * 100)}%` }}
          >
            <Star
              className={cn(
                sizeClasses[size],
                "fill-yellow-400 text-yellow-400"
              )}
            />
          </div>
        )}
      </button>
    );
  });

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <div className="flex">{stars}</div>
      {showNumber && (
        <span className="text-sm font-medium text-gray-700 ml-2">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default StarRating;
